// @ts-nocheck
import React from 'react';
import { smsSenderOrAuto } from '@/services/dialer/smsSender';
import { smsDelivery } from '@/services/dialer/smsThread';
import { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { MessageSquare, Send, Loader2, Sparkles, User, Search, Mail, Paperclip, X, Pencil, Check, Trash2, Plus } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { api } from '@/api/api-client';
import { toast } from 'sonner';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { applyTemplate, getTemplateVariables } from '@/utils/templateUtils';
import { formatDate, formatTime, formatDateTime } from '@/i18n/locale';
import { useDialerData } from '@/contexts/DialerDataContext';
import { useDialerAuth } from '@/contexts/DialerAuthContext';
import { useDialerTelephony } from '@/contexts/DialerTelephonyContext';
import { usePermissions } from '@/hooks/usePermissions';

// Defaults used to seed MongoDB on first load per client.
// After seeding, all CRUD goes through api.entities.MessageTemplate.
// The copy comes from the catalogs, so a customer is seeded in the language
// its agent works in. The [Nom] / [heure] / [date] / [lien] tokens are
// substituted later by applyTemplate and personalizeMessage, so they stay
// identical in every language.
const getSMSTemplates = (t, companyName) => [
    {
        id: 1,
        title: t('pages:smsPanel.templates.sms.firstContact.title'),
        category: t('pages:smsPanel.templates.categories.prospecting'),
        message: t('pages:smsPanel.templates.sms.firstContact.message', { company: companyName })
    },
    {
        id: 2,
        title: t('pages:smsPanel.templates.sms.meetingReminder.title'),
        category: t('pages:smsPanel.templates.categories.followUp'),
        message: t('pages:smsPanel.templates.sms.meetingReminder.message')
    },
    {
        id: 3,
        title: t('pages:smsPanel.templates.sms.postCallFollowUp.title'),
        category: t('pages:smsPanel.templates.categories.followUp'),
        message: t('pages:smsPanel.templates.sms.postCallFollowUp.message')
    },
    {
        id: 4,
        title: t('pages:smsPanel.templates.sms.specialOffer.title'),
        category: t('pages:smsPanel.templates.categories.sales'),
        message: t('pages:smsPanel.templates.sms.specialOffer.message', { company: companyName })
    }
];

const getEmailTemplates = (t, companyName) => [
    {
        id: 1,
        title: t('pages:smsPanel.templates.email.servicesIntro.title'),
        category: t('pages:smsPanel.templates.categories.prospecting'),
        subject: t('pages:smsPanel.templates.email.servicesIntro.subject'),
        message: t('pages:smsPanel.templates.email.servicesIntro.message', { company: companyName })
    },
    {
        id: 2,
        title: t('pages:smsPanel.templates.email.afterCall.title'),
        category: t('pages:smsPanel.templates.categories.followUp'),
        subject: t('pages:smsPanel.templates.email.afterCall.subject', { company: companyName }),
        message: t('pages:smsPanel.templates.email.afterCall.message', { company: companyName })
    },
    {
        id: 3,
        title: t('pages:smsPanel.templates.email.meetingReminder.title'),
        category: t('pages:smsPanel.templates.categories.followUp'),
        subject: t('pages:smsPanel.templates.email.meetingReminder.subject'),
        message: t('pages:smsPanel.templates.email.meetingReminder.message', { company: companyName })
    },
    {
        id: 4,
        title: t('pages:smsPanel.templates.email.specialOffer.title'),
        category: t('pages:smsPanel.templates.categories.sales'),
        subject: t('pages:smsPanel.templates.email.specialOffer.subject', { company: companyName }),
        message: t('pages:smsPanel.templates.email.specialOffer.message', { company: companyName })
    }
];

const getPDFAttachments = (t, companyName) => [
    { id: 1, name: t('pages:smsPanel.attachments.catalogue', { company: companyName }), url: '/attachments/catalogue.pdf' },
    { id: 2, name: t('pages:smsPanel.attachments.pricing', { company: companyName }), url: '/attachments/tarifs.pdf' },
    { id: 3, name: t('pages:smsPanel.attachments.presentation'), url: '/attachments/presentation.pdf' },
    { id: 4, name: t('pages:smsPanel.attachments.practicalInfo'), url: '/attachments/info.pdf' }
];

export default function SMSPanel({ clientEmail, lockedMode, showHistory = true }) {
    const { t } = useTranslation(['pages', 'common']);
    const navigate = useNavigate();
    const { selectedCustomerId } = useDialerData();
    const { selectedCallerId, activeCallerNumbers } = useDialerTelephony();
    // SMS-capable numbers : on a déjà la liste des numéros actifs du tenant
    // (cf DialerContext). Si aucun n'a la capability 'sms', le bouton SMS
    // ne sert à rien — bannière CTA vers Settings/Numéros.
    const smsCapableNumbers = activeCallerNumbers.filter((n) => Array.isArray(n.capabilities) ? n.capabilities.includes('sms') : true);
    const { data: emailCapabilities, isLoading: emailCapsLoading } = useQuery({
        queryKey: ['email-capabilities'],
        queryFn: async () => {
            const r = await api.functions.invoke('emailCapabilities');
            return r?.data ?? r;
        },
        staleTime: 60_000,
    });
    // Pour le cold outreach commercial, on EXIGE que le caller ait connecté
    // sa propre boîte (Gmail/Outlook OAuth). Le SMTP système est réservé aux
    // notifs transactionnelles internes (reminders RDV, etc) et ne doit
    // jamais être utilisé pour des emails à des prospects — délivrabilité
    // catastrophique sans la signature DKIM/SPF de la boîte de l'agent.
    const callerEmailProvider = emailCapabilities?.callerProvider ?? null; // 'gmail' | 'outlook' | null
    const callerHasEmailConnected = !!callerEmailProvider;
    const [mode, setMode] = useState(lockedMode || 'sms'); // 'sms' or 'email'
    const [selectedProspect, setSelectedProspect] = useState(null);

    const [prospectSearch, setProspectSearch] = useState('');
    const [message, setMessage] = useState('');
    const [emailSubject, setEmailSubject] = useState('');
    const [prospectEmail, setProspectEmail] = useState('');
    const [selectedAttachments, setSelectedAttachments] = useState([]);
    const [isSending, setIsSending] = useState(false);
    const [showProspectList, setShowProspectList] = useState(false);
    const [previousProspectName, setPreviousProspectName] = useState(null);
    const [selectedSource, setSelectedSource] = useState('all');

    // From the session, not the localStorage copy, which can be stale.
    const { callerData: sessionCaller } = useDialerAuth();
    const callerData = sessionCaller || {};
    const companyName = callerData.client_company || t('pages:smsPanel.defaultCompany');

    const PDF_ATTACHMENTS = getPDFAttachments(t, companyName);

    // ── Templates backed by MongoDB (MessageTemplate entity) ─────────────────
    // ADR-024 phase 2 — templates are per-Customer. Filter on (client_email,
    // customer_id) so each B2B customer has its own SMS/email library. The
    // query key includes selectedCustomerId so a sidebar switch refreshes.
    const queryClient = useQueryClient();
    // ADR-024 — keyed by selectedCustomerId so a sidebar switch refreshes,
    // but read isFetched (not isLoading) for the seed guard so that the
    // post-switch render — when data:[] is the cache default but no fetch
    // has completed yet — does NOT trigger a duplicate seed.
    const {
        data: allTemplates = [],
        isLoading: templatesLoading,
        isFetched: templatesFetched,
        dataUpdatedAt: templatesUpdatedAt,
    } = useQuery({
        queryKey: ['message-templates', clientEmail, selectedCustomerId],
        queryFn: () => api.entities.MessageTemplate.filter(
            { customer_id: selectedCustomerId || '' },
            '-created_date',
            200,
        ),
        enabled: !!clientEmail,
    });

    // First-time seed: if the active Customer has no templates yet, insert
    // defaults once. Per-Customer seed (not per-tenant) so each new customer
    // gets its own library to start from. We track which Customer ids have
    // already been seeded *in this session* via a ref so React re-renders
    // never trigger a re-seed (the previous useState approach lost its guard
    // on the post-switch render — data:[] + isLoading:false momentarily).
    const seededCustomersRef = useRef<Set<string>>(new Set());
    useEffect(() => {
        const customerKey = selectedCustomerId || '';
        if (!clientEmail) return;
        if (!templatesFetched) return; // wait for the actual API response
        if (seededCustomersRef.current.has(customerKey)) return;
        if (allTemplates.length > 0) {
            // Already has templates — mark as seeded so we don't probe again.
            seededCustomersRef.current.add(customerKey);
            return;
        }
        seededCustomersRef.current.add(customerKey);
        const smsSeeds = getSMSTemplates(t, companyName).map((tpl) => ({
            customer_id: customerKey,
            kind: 'sms',
            title: tpl.title,
            category: tpl.category || '',
            subject: '',
            message: tpl.message,
            is_default: true,
        }));
        const emailSeeds = getEmailTemplates(t, companyName).map((tpl) => ({
            customer_id: customerKey,
            kind: 'email',
            title: tpl.title,
            category: tpl.category || '',
            subject: tpl.subject || '',
            message: tpl.message,
            is_default: true,
        }));
        Promise.all(
            [...smsSeeds, ...emailSeeds].map((payload) =>
                api.entities.MessageTemplate.create(payload).catch(() => null),
            ),
        ).finally(() => {
            queryClient.invalidateQueries({ queryKey: ['message-templates', clientEmail, selectedCustomerId] });
        });
    }, [clientEmail, selectedCustomerId, templatesFetched, templatesUpdatedAt, allTemplates.length, companyName, queryClient]);

    const templates = allTemplates.filter((tpl) => tpl.kind === mode);

    // Inline edit state
    const [editingId, setEditingId] = useState(null);
    const [editDraft, setEditDraft] = useState({ title: '', subject: '', message: '', category: '' });
    const [savingId, setSavingId] = useState(null);

    const startEdit = (tpl) => {
        setEditingId(tpl.id);
        setEditDraft({
            title: tpl.title || '',
            subject: tpl.subject || '',
            message: tpl.message || '',
            category: tpl.category || '',
        });
    };
    const cancelEdit = () => {
        setEditingId(null);
        setEditDraft({ title: '', subject: '', message: '', category: '' });
    };
    const saveEdit = async (tplId) => {
        if (!editDraft.title.trim() || !editDraft.message.trim()) {
            toast.error(t('pages:smsPanel.toasts.titleAndMessageRequired'));
            return;
        }
        setSavingId(tplId);
        try {
            await api.entities.MessageTemplate.update(tplId, {
                title: editDraft.title.trim(),
                subject: mode === 'email' ? editDraft.subject.trim() : '',
                message: editDraft.message,
                category: editDraft.category.trim(),
            });
            queryClient.invalidateQueries({ queryKey: ['message-templates', clientEmail, selectedCustomerId] });
            cancelEdit();
            toast.success(t('pages:smsPanel.toasts.templateSaved'));
        } catch {
            toast.error(t('pages:smsPanel.toasts.saveError'));
        } finally {
            setSavingId(null);
        }
    };
    const [templateToDelete, setTemplateToDelete] = useState(null);
    // AUDIT-F23: templates are shared; only a tenant admin deletes one.
    const { isTenantAdmin } = usePermissions();
    const requestDeleteTemplate = (tpl) => setTemplateToDelete(tpl);
    const confirmDeleteTemplate = async () => {
        const tpl = templateToDelete;
        if (!tpl) return;
        const tplId = tpl.id;
        setTemplateToDelete(null);
        try {
            await api.entities.MessageTemplate.delete(tplId);
            queryClient.invalidateQueries({ queryKey: ['message-templates', clientEmail, selectedCustomerId] });
            if (editingId === tplId) cancelEdit();
            toast.success(t('pages:smsPanel.toasts.templateDeleted'));
        } catch {
            toast.error(t('pages:smsPanel.toasts.deleteError'));
        }
    };
    const createTemplate = async () => {
        try {
            // ADR-024 phase 2 — bind to active Customer.
            const created = await api.entities.MessageTemplate.create({
                customer_id: selectedCustomerId || '',
                kind: mode,
                title: t('pages:smsPanel.templates.newTitle'),
                category: '',
                subject: mode === 'email' ? t('pages:smsPanel.templates.newSubject') : '',
                message: t('pages:sms.placeholder'),
                is_default: false,
            });
            queryClient.invalidateQueries({ queryKey: ['message-templates', clientEmail, selectedCustomerId] });
            startEdit(created);
            toast.success(t('pages:smsPanel.toasts.templateCreated'));
        } catch {
            toast.error(t('pages:smsPanel.toasts.createError'));
        }
    };

    // Fetch prospects from queue (on charge large, filtrage cote client par liste + search)
    const { data: prospects = [] } = useQuery({
        queryKey: ['prospects-sms', clientEmail],
        queryFn: () => api.entities.ProspectQueue.filter({}, '-created_date', 1000),
        enabled: !!clientEmail,
    });

    // Liste des sources disponibles pour le filtre
    const availableSources = React.useMemo(() => {
        const set = new Set();
        for (const p of prospects) if (p.source) set.add(p.source);
        return Array.from(set).sort();
    }, [prospects]);

    // Filtrage : par mode de contact (mode SMS = besoin d'un téléphone,
    // mode Email = besoin d'une adresse email), puis par source (liste),
    // puis par texte libre.
    const filteredProspects = prospects.filter(p => {
        // Le mode SMS n'a aucun intérêt sur un prospect sans téléphone, idem
        // mode Email sans email. On les retire de la liste pour que l'agent
        // n'ait pas à scroller au travers de prospects non-actionnables.
        if (mode === 'sms' && !p.prospect_phone?.trim()) return false;
        if (mode === 'email' && !p.prospect_email?.trim()) return false;
        if (selectedSource !== 'all' && (p.source || 'Sans liste') !== selectedSource) return false;
        if (!prospectSearch) return true;
        const q = prospectSearch.toLowerCase();
        return (
            p.prospect_name?.toLowerCase().includes(q) ||
            p.prospect_company?.toLowerCase().includes(q) ||
            (mode === 'sms' && p.prospect_phone?.includes(prospectSearch)) ||
            (mode === 'email' && p.prospect_email?.toLowerCase().includes(q))
        );
    });

    // Un seul magasin depuis le 2026-09-28 : il porte l'entrant et le sortant,
    // tous opérateurs confondus. Avant, le sortant était lu en direct chez
    // Twilio et tout ce qui arrivait était étiqueté « envoyé ».
    const { data: allMessages = [], refetch } = useQuery({
        queryKey: ['sms-messages'],
        queryFn: async () => {
            const { data } = await api.functions.invoke('listInboundSms', { limit: 200 });
            return data.messages || [];
        },
        refetchInterval: 10000,
    });

    const messageHistory = React.useMemo(() => {
        return (allMessages || [])
            .map((m) => ({
                key: m.id || `${m.direction}-${m.received_at}`,
                direction: m.direction === 'inbound' ? 'inbound' : 'outbound',
                party: m.direction === 'inbound' ? m.from : m.to,
                body: m.body,
                at: m.received_at ? new Date(m.received_at).getTime() : 0,
                status: m.direction === 'inbound' ? null : m.status,
            }))
            .sort((a, b) => a.at - b.at);
    }, [allMessages]);

    const handleSend = async () => {
        if (!selectedProspect || !message) {
            toast.error(t('pages:smsPanel.toasts.prospectAndMessageRequired'));
            return;
        }

        if (mode === 'email' && !emailSubject) {
            toast.error(t('pages:smsPanel.toasts.subjectRequired'));
            return;
        }

        if (mode === 'email' && !prospectEmail) {
            toast.error(t('pages:smsPanel.toasts.prospectEmailRequired'));
            return;
        }

        setIsSending(true);
        try {
            if (mode === 'sms') {
                // `from` est optionnel : si vide, le serveur selectionne le 1er
                // numero actif du tenant. Si anti-spoofing echoue ou si aucun
                // numero provisionne, le serveur renvoie 412/403 avec message
                // explicite (affiche via err.data.error dans le catch).
                await api.functions.invoke('twilioSendSMS', {
                    to: selectedProspect.prospect_phone,
                    message: message,
                    from: smsSenderOrAuto(selectedCallerId, activeCallerNumbers),
                });
                toast.success(t('pages:smsPanel.toasts.smsSent'));
            } else {
                // Construct email body with attachments info
                let emailBody = message;
                if (selectedAttachments.length > 0) {
                    emailBody += `\n\n---\n${t('pages:smsPanel.compose.attachmentsBodyHeader')}\n`;
                    selectedAttachments.forEach(att => {
                        emailBody += `- ${att.name}\n`;
                    });
                }

                await api.integrations.Core.SendEmail({
                    to: prospectEmail,
                    subject: emailSubject,
                    body: emailBody,
                    from_name: callerData.caller_name || companyName
                });
                toast.success(t('pages:smsPanel.toasts.emailSent'));
            }
            
            setMessage('');
            setEmailSubject('');
            setProspectEmail('');
            setSelectedAttachments([]);
            setSelectedProspect(null);
            setProspectSearch('');
            refetch();
        } catch (error) {
            // Remonte le message backend explicite (numero Twilio manquant,
            // destinataire invalide, etc.) au lieu d'un generique.
            // api-client.js attache le body JSON sur err.data (cf. request()).
            const detail = error?.data?.error || error?.data?.message || error?.message || t('pages:smsPanel.toasts.unknownError');
            toast.error(t('pages:smsPanel.toasts.sendFailed', { detail }));
        } finally {
            setIsSending(false);
        }
    };

    const handleSelectProspect = (prospect) => {
        setSelectedProspect(prospect);
        setProspectSearch(prospect.prospect_name);
        setShowProspectList(false);
    };

    // Fetch meetings for selected prospect
    const { data: prospectMeetings = [] } = useQuery({
        queryKey: ['prospect-meetings', selectedProspect?.id],
        queryFn: async () => {
            if (!selectedProspect) return [];
            const meetings = await api.entities.ParallelCall.filter({
                prospect_phone: selectedProspect.prospect_phone,
                call_outcome: 'meeting_booked'
            }, '-created_date', 5);
            return meetings;
        },
        enabled: !!selectedProspect,
    });

    // Personalize message using shared template utility
    const personalizeMessage = (msg) => {
        let personalized = applyTemplate(msg, selectedProspect || {}, callerData);
        // Legacy compat: also replace [Votre nom] and meeting-specific vars
        if (callerData.caller_name) {
            personalized = personalized.replace(/\[Votre nom\]/g, callerData.caller_name);
        }
        personalized = personalized.replace(/\[domaine\]/g, 'Digital');
        if (prospectMeetings.length > 0) {
            const meeting = prospectMeetings[0];
            const meetingDate = new Date(meeting.created_date);
            const dateStr = formatDate(meetingDate, { weekday: 'long', day: 'numeric', month: 'long' });
            const timeStr = formatTime(meetingDate);
            personalized = personalized.replace(/\[date\]/g, dateStr);
            personalized = personalized.replace(/\[heure\]/g, timeStr);
        }
        return personalized;
    };

    // Auto-update message when prospect changes
    useEffect(() => {
        if (selectedProspect && message) {
            let updatedMessage = message;
            
            // Replace old prospect name with new one
            if (previousProspectName && previousProspectName !== selectedProspect.prospect_name) {
                updatedMessage = updatedMessage.replace(new RegExp(previousProspectName, 'g'), selectedProspect.prospect_name);
            }
            
            // Then personalize any remaining placeholders
            updatedMessage = personalizeMessage(updatedMessage);
            
            if (updatedMessage !== message) {
                setMessage(updatedMessage);
            }
            
            setPreviousProspectName(selectedProspect.prospect_name);
        }
    }, [selectedProspect]);

    const handleUseTemplate = (template) => {
        const personalizedMessage = personalizeMessage(template.message);
        setMessage(personalizedMessage);
        if (mode === 'email' && template.subject) {
            const personalizedSubject = personalizeMessage(template.subject);
            setEmailSubject(personalizedSubject);
        }
        toast.success(t('pages:smsPanel.toasts.templateApplied'));
    };

    const toggleAttachment = (attachment) => {
        if (selectedAttachments.find(a => a.id === attachment.id)) {
            setSelectedAttachments(selectedAttachments.filter(a => a.id !== attachment.id));
        } else {
            setSelectedAttachments([...selectedAttachments, attachment]);
        }
    };



    const isEmptyState =
        (mode === 'sms' && smsCapableNumbers.length === 0) ||
        (mode === 'email' && !emailCapsLoading && !callerHasEmailConnected);

    return (
        <div className={isEmptyState ? 'max-w-2xl mx-auto space-y-6' : 'grid grid-cols-1 lg:grid-cols-3 gap-6'}>
            {/* Left: Send SMS/Email Form + History */}
            <div className={isEmptyState ? 'space-y-6' : 'lg:col-span-2 space-y-6'}>
                {/* Mode Toggle — only shown when not locked to a single mode */}
                {!lockedMode && (
                    <Tabs value={mode} onValueChange={setMode} className="w-full">
                        <TabsList className="grid w-full grid-cols-2 bg-muted">
                            <TabsTrigger value="sms" className="flex items-center gap-2">
                                <MessageSquare className="w-4 h-4" />
                                SMS
                            </TabsTrigger>
                            <TabsTrigger value="email" className="flex items-center gap-2">
                                <Mail className="w-4 h-4" />
                                {t('common:field.email')}
                            </TabsTrigger>
                        </TabsList>
                    </Tabs>
                )}

                {/* Pre-flight CTA si moyen de contact pas configuré */}
                {mode === 'sms' && smsCapableNumbers.length === 0 && (
                    <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3">
                        <MessageSquare className="w-5 h-5 text-amber-600 shrink-0 mt-0.5" />
                        <div className="flex-1">
                            <p className="font-semibold text-sm text-foreground">{t('pages:smsPanel.empty.noSmsNumber.title')}</p>
                            <p className="text-xs text-muted-foreground mt-1">{t('pages:smsPanel.empty.noSmsNumber.body')}</p>
                            <Button
                                size="sm"
                                variant="outline"
                                className="mt-3 h-8 text-xs"
                                onClick={() => navigate('/ParallelDialer/settings?section=numbers')}
                            >
                                {t('pages:smsPanel.empty.noSmsNumber.cta')}
                            </Button>
                        </div>
                    </div>
                )}
                {/* Kevin, 2026-09-30 : le bandeau jaune « aucune boite mail »
                    a ete retire. L'etat vide de l'historique porte deja le
                    bouton qui mene au bon reglage ; deux avertissements pour
                    la meme chose, dont un en couleur d'alerte, c'est un de
                    trop. */}

                {/* Send Form + History — masqués en empty state (CTA seul) */}
                {!isEmptyState && (<>
                <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                        {mode === 'sms' ? (
                            <>
                                <MessageSquare className="w-5 h-5 text-[var(--vf-accent-text)]" />
                                {t('pages:smsPanel.compose.smsHeading')}
                            </>
                        ) : (
                            <>
                                <Mail className="w-5 h-5 text-blue-600" />
                                {t('pages:smsPanel.compose.emailHeading')}
                            </>
                        )}
                    </h3>
                    <div className="space-y-4">
                        {/* Prospect Search — filtre par liste puis recherche texte */}
                        <div className="relative">
                            <label className="text-sm font-medium text-foreground/80 mb-1.5 block">{t('pages:smsPanel.compose.recipient')}</label>
                            {availableSources.length > 1 && (
                                <div className="mb-2">
                                    <select
                                        value={selectedSource}
                                        onChange={(e) => setSelectedSource(e.target.value)}
                                        className="w-full h-9 text-sm px-3 rounded-lg border border-border bg-background cursor-pointer"
                                    >
                                        <option value="all">{t('pages:smsPanel.compose.allLists', { total: prospects.length })}</option>
                                        {availableSources.map(src => (
                                            <option key={src} value={src}>
                                                {src} ({prospects.filter(p => (p.source || 'Sans liste') === src).length})
                                            </option>
                                        ))}
                                    </select>
                                </div>
                            )}
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground/60" />
                                <Input
                                    placeholder={t('pages:smsPanel.compose.searchPlaceholder')}
                                    value={prospectSearch}
                                    onChange={(e) => {
                                        setProspectSearch(e.target.value);
                                        setShowProspectList(true);
                                    }}
                                    onFocus={() => setShowProspectList(true)}
                                    className="surface-1 border-border pl-10"
                                />
                            </div>

                            {/* Prospect Dropdown */}
                            {showProspectList && filteredProspects.length > 0 && (
                                <div className="absolute z-10 w-full mt-1 bg-card border border-border rounded-lg shadow-lg max-h-60 overflow-y-auto">
                                    {filteredProspects.slice(0, 20).map((prospect) => (
                                        <button
                                            key={prospect.id}
                                            onClick={() => {
                                                setSelectedProspect(prospect);
                                                setProspectSearch(prospect.prospect_name);
                                                setShowProspectList(false);
                                                if (mode === 'email' && prospect.prospect_email) {
                                                    setProspectEmail(prospect.prospect_email);
                                                } else if (mode === 'email') {
                                                    setProspectEmail('');
                                                }
                                            }}
                                            className="w-full px-4 py-3 hover:bg-accent/50 transition-colors text-left flex items-center gap-3"
                                        >
                                            <div className="w-8 h-8 rounded-full bg-[var(--vf-accent-bg)] flex items-center justify-center flex-shrink-0">
                                                <User className="w-4 h-4 text-[var(--vf-accent-text)]" />
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-foreground truncate">
                                                    {prospect.prospect_name}
                                                    {prospect.prospect_company && <span className="text-muted-foreground font-normal"> · {prospect.prospect_company}</span>}
                                                </p>
                                                <p className="text-xs text-muted-foreground flex items-center gap-2">
                                                    <span className="font-mono truncate">
                                                        {mode === 'email' ? prospect.prospect_email : prospect.prospect_phone}
                                                    </span>
                                                    {prospect.source && <span className="px-1.5 py-0.5 rounded bg-muted text-[10px] shrink-0">{prospect.source}</span>}
                                                </p>
                                            </div>
                                        </button>
                                    ))}
                                </div>
                            )}

                            {/* Selected Prospect */}
                            {selectedProspect && (
                                <div className="mt-2 flex items-center gap-2 bg-[var(--vf-accent-bg)] border border-[var(--vf-accent-bd)] rounded-lg px-3 py-2">
                                    <User className="w-4 h-4 text-[var(--vf-accent-text)]" />
                                    <div className="flex-1 min-w-0">
                                        <p className="text-sm font-medium text-foreground truncate">{selectedProspect.prospect_name}</p>
                                        <p className="text-xs text-muted-foreground font-mono truncate">
                                            {mode === 'email'
                                                ? (selectedProspect.prospect_email || prospectEmail || '—')
                                                : selectedProspect.prospect_phone}
                                        </p>
                                    </div>
                                    <button
                                        onClick={() => {
                                            setSelectedProspect(null);
                                            setProspectSearch('');
                                        }}
                                        className="text-muted-foreground/60 hover:text-muted-foreground"
                                    >
                                        ×
                                    </button>
                                </div>
                            )}
                        </div>

                        {/* Email Address (only in email mode if prospect doesn't have email) */}
                        {mode === 'email' && selectedProspect && !selectedProspect.prospect_email && (
                            <div>
                                <label className="text-sm font-medium text-foreground/80 mb-1.5 block">
                                    {t('pages:smsPanel.compose.prospectEmailLabel')}
                                </label>
                                <Input
                                    type="email"
                                    placeholder={t('pages:smsPanel.compose.prospectEmailPlaceholder')}
                                    value={prospectEmail}
                                    onChange={(e) => setProspectEmail(e.target.value)}
                                    className="bg-yellow-50 border-yellow-300"
                                />
                                <p className="text-xs text-yellow-600 mt-1">
                                    {t('pages:smsPanel.compose.prospectEmailHint')}
                                </p>
                            </div>
                        )}

                        {/* Email Subject (only in email mode) */}
                        {mode === 'email' && (
                            <div>
                                <label className="text-sm font-medium text-foreground/80 mb-1.5 block">{t('pages:smsPanel.compose.subjectLabel')}</label>
                                <Input
                                    placeholder={t('pages:smsPanel.compose.subjectPlaceholder')}
                                    value={emailSubject}
                                    onChange={(e) => setEmailSubject(e.target.value)}
                                    className="surface-1 border-border"
                                />
                            </div>
                        )}

                        {/* Message */}
                        <div>
                            <label className="text-sm font-medium text-foreground/80 mb-1.5 block">{t('pages:smsPanel.compose.messageLabel')}</label>
                            <Textarea
                                placeholder={mode === 'sms' ? t('pages:sms.placeholder') : t('pages:smsPanel.compose.emailBodyPlaceholder')}
                                value={message}
                                onChange={(e) => setMessage(e.target.value)}
                                rows={mode === 'email' ? 10 : 6}
                                className="surface-1 border-border"
                            />
                            <p className="text-xs text-muted-foreground mt-1">{t('pages:smsPanel.compose.charCount', { count: message.length })}</p>
                        </div>

                        {/* Attachments (only in email mode) */}
                        {mode === 'email' && (
                            <div>
                                <label className="text-sm font-medium text-foreground/80 mb-2 block flex items-center gap-2">
                                    <Paperclip className="w-4 h-4" />
                                    {t('pages:smsPanel.compose.attachments')}
                                </label>
                                <div className="grid grid-cols-2 gap-2">
                                    {PDF_ATTACHMENTS.map((attachment) => (
                                        <button
                                            key={attachment.id}
                                            onClick={() => toggleAttachment(attachment)}
                                            className={`flex items-center gap-2 px-3 py-2 rounded-lg border transition-all text-left ${
                                                selectedAttachments.find(a => a.id === attachment.id)
                                                    ? 'bg-blue-50 border-blue-300 text-blue-900'
                                                    : 'surface-1 border-border hover:bg-accent'
                                            }`}
                                        >
                                            <Paperclip className="w-4 h-4 flex-shrink-0" />
                                            <span className="text-xs truncate">{attachment.name}</span>
                                        </button>
                                    ))}
                                </div>
                                {selectedAttachments.length > 0 && (
                                    <div className="mt-2 flex flex-wrap gap-2">
                                        {selectedAttachments.map((att) => (
                                            <Badge key={att.id} className="bg-blue-100 text-blue-700 border-0 pr-1">
                                                {att.name}
                                                <button
                                                    onClick={() => toggleAttachment(att)}
                                                    className="ml-1 hover:bg-blue-200 rounded-full p-0.5"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </Badge>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}

                        <Button
                            onClick={handleSend}
                            disabled={
                                isSending
                                || !selectedProspect
                                || !message
                                || (mode === 'sms' && smsCapableNumbers.length === 0)
                                || (mode === 'email' && !callerHasEmailConnected)
                            }
                            className="w-full bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)]"
                        >
                            {isSending ? (
                                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                            ) : (
                                <Send className="w-4 h-4 mr-2" />
                            )}
                            {mode === 'sms' ? t('pages:smsPanel.compose.sendSms') : t('pages:smsPanel.compose.sendEmail')}
                        </Button>
                    </div>
                </div>

                {/* Message History: every number mixed. Off where the
                    conversations list stands beside the form. */}
                {showHistory && (
                <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6">
                    <h3 className="text-lg font-semibold text-foreground mb-4">{t('pages:sms.messageHistory')}</h3>
                    {messageHistory.length === 0 ? (
                        <div className="text-center py-12">
                            <MessageSquare className="w-12 h-12 text-muted-foreground/40 mx-auto mb-3" />
                            <p className="text-muted-foreground">{t('pages:smsPanel.history.empty')}</p>
                        </div>
                    ) : (
                        <div className="space-y-2 max-h-96 overflow-y-auto px-1">
                            {messageHistory.map((msg, idx) => {
                                const isInbound = msg.direction === 'inbound';
                                return (
                                <motion.div
                                    key={msg.key}
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    transition={{ delay: Math.min(idx, 10) * 0.03 }}
                                    className={`flex ${isInbound ? 'justify-start' : 'justify-end'}`}
                                >
                                    <div
                                        className={`max-w-[78%] rounded-2xl px-3.5 py-2 ${
                                            isInbound
                                                ? 'bg-muted text-foreground rounded-bl-sm'
                                                : 'bg-[var(--vf-primary)] text-white rounded-br-sm'
                                        }`}
                                    >
                                        <div className={`text-[10px] mb-0.5 flex items-center gap-1.5 ${isInbound ? 'text-muted-foreground' : 'text-white/70'}`}>
                                            <span className="font-mono">{msg.party || '—'}</span>
                                            {isInbound && (
                                                <span className="px-1 py-0.5 rounded bg-emerald-500/15 text-emerald-600 font-semibold">{t('pages:smsPanel.history.inbound')}</span>
                                            )}
                                        </div>
                                        <p className="text-sm whitespace-pre-wrap break-words">{msg.body}</p>
                                        <div className={`text-[10px] mt-0.5 flex items-center gap-2 ${isInbound ? 'text-muted-foreground' : 'text-white/70'}`}>
                                            <span>{msg.at ? formatDateTime(msg.at) : ''}</span>
                                            {!isInbound && smsDelivery(msg.status) && <span>{t(`pages:sms.delivery.${smsDelivery(msg.status)}`)}</span>}
                                        </div>
                                    </div>
                                </motion.div>
                                );
                            })}
                        </div>
                    )}
                </div>
                )}
                </>)}
            </div>

            {/* Right: Templates — masqué en empty state */}
            {!isEmptyState && (
            <div className="space-y-6">
                <div className="bg-card border border-border/50 rounded-xl shadow-sm p-6">
                    <div className="flex items-center justify-between mb-1">
                        <h3 className="text-lg font-semibold text-foreground flex items-center gap-2">
                            <Sparkles className="w-5 h-5 text-[var(--vf-accent-text)]" />
                            {mode === 'sms' ? t('pages:smsPanel.templates.headingSms') : t('pages:smsPanel.templates.headingEmail')}
                        </h3>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={createTemplate}
                            disabled={!clientEmail}
                            className="h-7 px-2 text-xs"
                        >
                            <Plus className="w-3 h-3 mr-1" />
                            {t('pages:smsPanel.templates.new')}
                        </Button>
                    </div>
                    <p className="text-xs text-muted-foreground mb-4">{t('pages:smsPanel.templates.hint')}</p>

                    <div className="space-y-3">
                        {templatesLoading && (
                            <div className="text-center py-6 text-xs text-muted-foreground">
                                <Loader2 className="w-4 h-4 animate-spin mx-auto mb-2" />
                                {t('common:actions.loading')}
                            </div>
                        )}
                        {!templatesLoading && templates.length === 0 && (
                            <div className="text-center py-6 text-xs text-muted-foreground">
                                {t('pages:smsPanel.templates.empty')}
                            </div>
                        )}
                        {templates.map((template) => {
                            const isEditing = editingId === template.id;
                            const isSaving = savingId === template.id;
                            if (isEditing) {
                                return (
                                    <div
                                        key={template.id}
                                        className="bg-card border border-[var(--vf-accent-bd)] rounded-lg p-4 space-y-2 shadow-sm"
                                    >
                                        <Input
                                            value={editDraft.title}
                                            onChange={(e) => setEditDraft({ ...editDraft, title: e.target.value })}
                                            placeholder={t('pages:smsPanel.templates.titlePlaceholder')}
                                            className="text-sm font-semibold h-8"
                                        />
                                        <Input
                                            value={editDraft.category}
                                            onChange={(e) => setEditDraft({ ...editDraft, category: e.target.value })}
                                            placeholder={t('pages:smsPanel.templates.categoryPlaceholder')}
                                            className="text-xs h-7"
                                        />
                                        {mode === 'email' && (
                                            <Input
                                                value={editDraft.subject}
                                                onChange={(e) => setEditDraft({ ...editDraft, subject: e.target.value })}
                                                placeholder={t('pages:smsPanel.templates.subjectPlaceholder')}
                                                className="text-xs h-7"
                                            />
                                        )}
                                        <Textarea
                                            value={editDraft.message}
                                            onChange={(e) => setEditDraft({ ...editDraft, message: e.target.value })}
                                            rows={mode === 'email' ? 8 : 4}
                                            placeholder={t('pages:smsPanel.templates.messagePlaceholder')}
                                            className="text-xs"
                                        />
                                        <div className="flex items-center justify-between gap-2 pt-1">
                                            {isTenantAdmin ? (
                                            <button
                                                onClick={() => requestDeleteTemplate(template)}
                                                disabled={isSaving}
                                                className="text-xs text-destructive hover:underline flex items-center gap-1"
                                            >
                                                <Trash2 className="w-3 h-3" />
                                                {t('common:actions.delete')}
                                            </button>
                                            ) : <span />}
                                            <div className="flex items-center gap-2">
                                                <Button
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={cancelEdit}
                                                    disabled={isSaving}
                                                    className="h-7 px-2 text-xs"
                                                >
                                                    {t('common:actions.cancel')}
                                                </Button>
                                                <Button
                                                    size="sm"
                                                    onClick={() => saveEdit(template.id)}
                                                    disabled={isSaving}
                                                    className="h-7 px-3 text-xs bg-[var(--vf-primary)] hover:bg-[var(--vf-primary-hover)]"
                                                >
                                                    {isSaving ? (
                                                        <Loader2 className="w-3 h-3 mr-1 animate-spin" />
                                                    ) : (
                                                        <Check className="w-3 h-3 mr-1" />
                                                    )}
                                                    {t('common:actions.save')}
                                                </Button>
                                            </div>
                                        </div>
                                    </div>
                                );
                            }
                            return (
                                <div
                                    key={template.id}
                                    className="bg-[var(--vf-accent-bg)] relative border border-[var(--vf-accent-bd)] rounded-lg p-4 hover:shadow-md transition-all group"
                                >
                                    <div className="absolute top-2 right-2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                startEdit(template);
                                            }}
                                            title={t('common:actions.edit')}
                                            className="p-1.5 rounded-md bg-card/80 hover:bg-card"
                                        >
                                            <Pencil className="w-3 h-3 text-[var(--vf-accent-text)]" />
                                        </button>
                                        {isTenantAdmin && (
                                        <button
                                            type="button"
                                            onClick={(e) => {
                                                e.stopPropagation();
                                                requestDeleteTemplate(template);
                                            }}
                                            title={t('common:actions.delete')}
                                            className="p-1.5 rounded-md bg-card/80 hover:bg-card"
                                        >
                                            <Trash2 className="w-3 h-3 text-destructive" />
                                        </button>
                                        )}
                                    </div>
                                    <motion.button
                                        type="button"
                                        onClick={() => handleUseTemplate(template)}
                                        whileHover={{ scale: 1.01 }}
                                        whileTap={{ scale: 0.99 }}
                                        className="w-full text-left"
                                    >
                                        <div className="flex items-start justify-between mb-2 pr-8">
                                            <h4 className="font-semibold text-foreground text-sm">{template.title}</h4>
                                            {template.category && (
                                                <span className="text-xs bg-[var(--vf-accent-bg)] text-[var(--vf-accent-text)] px-2 py-0.5 rounded-full">
                                                    {template.category}
                                                </span>
                                            )}
                                        </div>
                                        {mode === 'email' && template.subject && (
                                            <p className="text-xs font-medium text-foreground/80 mb-1">{t('pages:smsPanel.templates.subjectPrefix')} {template.subject}</p>
                                        )}
                                        <p className="text-xs text-muted-foreground line-clamp-3">{template.message}</p>
                                        <div className="mt-2 flex items-center text-xs text-[var(--vf-accent-text)] opacity-0 group-hover:opacity-100 transition-opacity">
                                            <Sparkles className="w-3 h-3 mr-1" />
                                            {t('pages:smsPanel.templates.clickToUse')}
                                        </div>
                                    </motion.button>
                                </div>
                            );
                        })}
                    </div>
                </div>

                {/* Template Variables Reference */}
                <div className="bg-[var(--vf-accent-bg)] border border-blue-200 rounded-xl p-6">
                    <h4 className="font-semibold text-foreground text-sm mb-2">{t('pages:smsPanel.variables.title')}</h4>
                    <div className="space-y-1.5">
                        {getTemplateVariables().map((v) => (
                            <div key={v.variable} className="flex items-center justify-between text-xs">
                                <code className="bg-card/80 px-1.5 py-0.5 rounded text-[var(--vf-accent-text)] font-mono">{v.variable}</code>
                                <span className="text-muted-foreground truncate ml-2">{v.label}</span>
                            </div>
                        ))}
                    </div>
                    {selectedProspect && message && (
                        <div className="mt-3 pt-3 border-t border-blue-200">
                            <h5 className="text-xs font-medium text-foreground/80 mb-1">{t('pages:smsPanel.variables.preview')}</h5>
                            <p className="text-xs text-muted-foreground bg-card/60 rounded p-2 whitespace-pre-wrap break-words">{personalizeMessage(message)}</p>
                        </div>
                    )}
                </div>
            </div>
            )}

            {/* Confirm delete dialog (design system) */}
            <AlertDialog open={!!templateToDelete} onOpenChange={(open) => !open && setTemplateToDelete(null)}>
                <AlertDialogContent>
                    <AlertDialogHeader>
                        <AlertDialogTitle>{t('pages:smsPanel.templates.deleteTitle')}</AlertDialogTitle>
                        <AlertDialogDescription>
                            {templateToDelete?.title
                                ? t('pages:smsPanel.templates.deleteBodyNamed', { title: templateToDelete.title })
                                : t('pages:smsPanel.templates.deleteBody')}
                        </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                        <AlertDialogCancel>{t('common:actions.cancel')}</AlertDialogCancel>
                        <AlertDialogAction
                            onClick={confirmDeleteTemplate}
                            className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                        >
                            {t('common:actions.delete')}
                        </AlertDialogAction>
                    </AlertDialogFooter>
                </AlertDialogContent>
            </AlertDialog>
        </div>
    );
}